import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { KeyboardShortcut } from "@/components/KeyboardShortcut";
import { Row } from "@/components/Row";
import { FormRichTextInput } from "@/components/form/FormRichTextInput";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";

import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { tiptap } from "@/lib/tiptap";
import { cn } from "@/lib/utils";

const schema = z.object({
  justification: z.string().refine((value) => !tiptap.isEmptyFn(value), {
    message: "Justification is required.",
  }),
});

// Shared rejection-reason capture (tiptap justification + Cmd+Enter submit).
// Used by the candidate sheet's ConfirmRejectDropdown and the screening prompt.
export const RejectReasonForm = ({
  isCompact = false,
  isPending = false,
  label = "Justification",
  mixpanelScreen,
  onConfirm,
}: {
  // Tightens the editor and button for the corner screening prompt, which has
  // to stay near notification-popup size.
  isCompact?: boolean;
  isPending?: boolean;
  label?: string;
  mixpanelScreen: MIXPANEL_SCREENS;
  onConfirm: (justification: string) => void;
}) => {
  const form = useForm<z.infer<typeof schema>>({
    defaultValues: { justification: "" },
    resolver: zodResolver(schema),
  });

  const handleSubmit = (data: z.infer<typeof schema>) => {
    // Guards the Cmd+Enter path too, which bypasses the disabled button.
    if (isPending) return;
    mixpanelUtils.trackFormSubmission("Confirm Reject", mixpanelScreen);
    onConfirm(data.justification);
    form.reset();
  };

  // Document-level listener so Cmd+Enter works even when a portalled
  // dropdown/popover/editor has focus.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && e.metaKey) {
        e.preventDefault();
        mixpanelUtils.trackKeyboardShortcutUsed(
          "Cmd+Enter (Confirm Reject)",
          mixpanelScreen,
        );
        void form.handleSubmit(handleSubmit)();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isPending]);

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-2 text-sm"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormRichTextInput
          autoFocus
          editorHeight={isCompact ? "64px" : "128px"}
          fieldName="justification"
          form={form}
          label={label}
        />
        <Row className="justify-end pt-2">
          <Button
            className={cn(
              "gap-1.5 rounded-full",
              isCompact && "h-7 px-3 text-[11px] font-semibold",
            )}
            disabled={isPending}
            type="submit"
            variant="destructive"
          >
            Confirm Reject
            <KeyboardShortcut text="⌘↵" variant="onDestructive" />
          </Button>
        </Row>
      </form>
    </Form>
  );
};
